import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  Assessment as AssessmentIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  PauseCircle as PauseCircleIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import FinanceContainer from "../containers/FinanceContainer";
import { useRouter } from "../../../hooks/useRouter";
import { formatCurrency } from "../../../utils";
import pettyCashService from "../services/pettyCashService";
import { DashboardSummary } from "../types/pettyCash.types";

interface KpiCard {
  label: string;
  value: string;
  sub: string;
  icon: React.ReactNode;
  color: string;
}

interface NavCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
  badge?: string;
}

const FinanceDashboard: React.FC = () => {
  const router = useRouter();
  const [r2pSummary, setR2pSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await pettyCashService.getDashboardSummary().then(setR2pSummary);
    } catch {
      setError("Could not load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Request-to-Payment KPIs from petty cash summary
  const r2pKpis: KpiCard[] = [
    {
      label: "Pending Review",
      value: String(r2pSummary?.totalPending ?? 0),
      sub: formatCurrency(r2pSummary?.totalAmount?.pending ?? 0),
      icon: <HourglassIcon />,
      color: "#1C4697",
    },
    {
      label: "Approved — Awaiting Payment",
      value: String(r2pSummary?.totalApprovedAwaitingPayment ?? 0),
      sub: formatCurrency(r2pSummary?.totalAmount?.approved ?? 0),
      icon: <CheckCircleIcon />,
      color: "#2e7d32",
    },
    {
      label: "On Hold",
      value: String(r2pSummary?.totalOnHold ?? 0),
      sub: formatCurrency(r2pSummary?.totalAmount?.onHold ?? 0),
      icon: <PauseCircleIcon />,
      color: "#e65100",
    },
    {
      label: "Paid This Month",
      value: String(r2pSummary?.totalThisMonth ?? 0),
      sub: formatCurrency(r2pSummary?.totalAmount?.thisMonth ?? 0),
      icon: <AttachMoneyIcon />,
      color: "#6a1b9a",
    },
  ];

  const navCards: NavCard[] = [
    {
      title: "Request-to-Payment",
      description: "Small cash requests with multi-level approval workflow",
      icon: <AttachMoneyIcon sx={{ fontSize: 36 }} />,
      path: "/rtp",
      color: "#2e7d32",
      badge: "LIVE",
    },
    {
      title: "Reports",
      description: "R2P analytics, cashbook & cashflow reports",
      icon: <AssessmentIcon sx={{ fontSize: 36 }} />,
      path: "/finance/reports",
      color: "#4e342e",
    },
  ];

  return (
    <FinanceContainer>
      {/* Page Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="#12315A">
            Finance Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Live portfolio visibility — collections, payables, cash, and payment
            requests.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={fetchDashboard}
          disabled={loading}
          sx={{ borderColor: "#1C4697", color: "#1C4697", fontWeight: 600 }}
        >
          REFRESH
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* ── Request-to-Payment KPIs ── */}
      <Divider sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          fontWeight={700}
          letterSpacing={2}
        >
          REQUEST-TO-PAYMENT
        </Typography>
      </Divider>

      <Grid container spacing={2} sx={{ mb: 1 }}>
        {r2pKpis.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 4px 16px rgba(18,49,90,0.08)",
                borderLeft: `4px solid ${card.color}`,
                height: "100%",
                cursor: "pointer",
                transition: "transform 0.2s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              onClick={() => router.push("/rtp")}
            >
              <CardContent
                sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
              >
                <Box
                  sx={{
                    bgcolor: `${card.color}18`,
                    color: card.color,
                    borderRadius: 2,
                    p: 1.2,
                    display: "flex",
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={500}
                  >
                    {card.label}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color={card.color}>
                    {loading ? "—" : card.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {card.sub}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{ mb: 4, mt: 1 }}
      >
        <Button
          variant="text"
          size="small"
          endIcon={<AssessmentIcon fontSize="small" />}
          onClick={() => router.push("/finance/reports")}
          sx={{ color: "#1C4697", fontWeight: 600 }}
        >
          View R2P Reports
        </Button>
        <Button
          variant="text"
          size="small"
          endIcon={<AttachMoneyIcon fontSize="small" />}
          onClick={() => router.push("/rtp")}
          sx={{ color: "#2e7d32", fontWeight: 600 }}
        >
          Manage Requests
        </Button>
      </Stack>

      {/* ── Module Navigation ── */}
      <Divider sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          fontWeight={700}
          letterSpacing={2}
        >
          NAVIGATE TO MODULE
        </Typography>
      </Divider>

      <Grid container spacing={3}>
        {navCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.title}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(18,49,90,0.08)",
                height: "100%",
                transition: "all 0.25s ease",
                "&:hover": {
                  boxShadow: `0 8px 32px ${card.color}30`,
                  transform: "translateY(-4px)",
                },
              }}
            >
              <CardActionArea
                onClick={() => router.push(card.path)}
                sx={{ height: "100%", p: 0.5 }}
              >
                <CardContent sx={{ textAlign: "center", py: 3, px: 2.5 }}>
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      bgcolor: `${card.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                      color: card.color,
                      border: `2px solid ${card.color}30`,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" fontWeight={700} color="#12315A">
                      {card.title}
                    </Typography>
                    {card.badge && (
                      <Chip
                        label={card.badge}
                        size="small"
                        sx={{
                          bgcolor: card.color,
                          color: "white",
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          height: 20,
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.5 }}
                  >
                    {card.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </FinanceContainer>
  );
};

export default FinanceDashboard;
